import { Box, Divider, useDisclosure } from '@chakra-ui/react';
import { addListener } from '@reduxjs/toolkit';
import BrandKitImage from 'assets/images/brand_kit.png';
import { UPGRADE_REDIRECT_BIZ_LINK } from 'constants/links';
import { CLICK_ON_PAID_FEATURE, PALETTE_APPLIED, UPGRADE } from 'constants/mixpanel';
import { useVenngageE1Gate } from 'hooks/useVenngageE1Gate';
import { useChameleon } from 'libs/third-party/Chameleon';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { dispatchGlobalApplyThemeEvent } from 'modules/Editor/Editor.helpers';
import { CommonGatedUpgradeModal } from 'modules/Editor/components/Modals/CommonGatedUpgradeModal';
import { ModalUpgradeTypeEnum } from 'modules/Editor/components/Modals/CommonGatedUpgradeModal.types';
import { setActiveWidget, store, useAppDispatch } from 'modules/Editor/store';
import { applyColor, updatePageBackground } from 'modules/Editor/store/infographSlice';
import { selectActivePage } from 'modules/Editor/store/pageSelector';
import { UserTypes, useUser } from 'modules/User';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useSWRConfig } from 'swr';
import { debounce } from 'utils/debounce';
import { setAnalyticsUpgradeFeatureProp } from 'utils/sessionStorage';
import { NewWidget } from 'widgets/Widget.types';

import { ColorShuffle } from './ApplyColorTheme/colorShuffle';
import { ExploreFonts } from './ApplyFonts/ApplyFonts';
import { BrandKitTab } from './BrandKitTab';
import { ExploreTab } from './ExploreTab/ExploreTab';

export interface BrandKitMenuProps {
  onItemClick: (widget: NewWidget | NewWidget[]) => void;
  disableDragDrop?: boolean;
}

export const BrandKitMenu = memo(({ onItemClick, disableDragDrop }: BrandKitMenuProps) => {
  // Upgrade modal for non-biz users when they try to use brand kit
  const { isOpen: isUpgradeModal, onOpen: onOpenUpgradeModal, onClose: onCloseUpgradeModal } = useDisclosure();

  const { cache } = useSWRConfig();
  const [colorShuffle] = useState(new ColorShuffle(cache as Map<string, any>));

  const { trackChameleon } = useChameleon();
  const { isFreeUser, isPremiumUser, isGuestRole } = useUser();
  const { venngageE2 } = useVenngageE1Gate();

  // Note: Those users with unGatedBrandKit experience can use the brand kit
  const hasUnGatedBrandKit = (isFreeUser || isPremiumUser) && !!venngageE2?.experiments?.unGatedBrandKit;

  // Free and Premium users should not be able to use but see the logo's with business badge labeled.
  const hasBrandKitPermission = (!isFreeUser && !isPremiumUser && !isGuestRole) || hasUnGatedBrandKit;
  const dispatch = useAppDispatch();

  const upgradeMpFeatureName = useRef('');

  const handleOpenUpgradeModal = useCallback(
    (mpFeature = UPGRADE.feature.BrandkitPalette) => {
      upgradeMpFeatureName.current = mpFeature;
      onOpenUpgradeModal();
    },
    [onOpenUpgradeModal],
  );

  const handleShuffleColors = useCallback(
    (colors: string[], isBrandKit?: boolean) => {
      if (isBrandKit && !hasBrandKitPermission) {
        handleOpenUpgradeModal(UPGRADE.feature.BrandkitPalette);
        Mixpanel.track(CLICK_ON_PAID_FEATURE.eventName, {
          Feature: UPGRADE.feature.BrandkitPalette,
          plan: UserTypes.Business,
        });
        return;
      }

      const { updatePageColors, updateWidgets } = colorShuffle.shuffle(colors, store.getState().infograph);
      dispatch(setActiveWidget([]));
      dispatch(applyColor({ updatePageColors, updateWidgets, colors }));
      dispatchGlobalApplyThemeEvent();

      Mixpanel.track(PALETTE_APPLIED.eventName, {
        type: isBrandKit ? PALETTE_APPLIED.type.BrandKit : PALETTE_APPLIED.type.Generic,
      });
    },
    [hasBrandKitPermission, dispatch, handleOpenUpgradeModal, colorShuffle],
  );

  const onClickUpgrade = () => {
    const mpFeature = upgradeMpFeatureName.current || UPGRADE.feature.BrandkitPalette;
    setAnalyticsUpgradeFeatureProp(mpFeature);
    window.open(UPGRADE_REDIRECT_BIZ_LINK, '_self');
    Mixpanel.track(UPGRADE.eventName, {
      feature: mpFeature,
      plan: UserTypes.Business,
    });
    trackChameleon(UPGRADE.eventName);
  };

  // If the background color is directly changed from the updatePageBackground action
  // -> reset the background color of the colorShuffle
  const resetColorShuffleBackground = debounce(
    () => colorShuffle.resetBackground(selectActivePage(store.getState())),
    500,
  );
  useEffect(() => {
    return dispatch(addListener({ actionCreator: updatePageBackground, effect: resetColorShuffleBackground }));

    // resetColorShuffleBackground is using the same colorShuffle so no need to add it as a dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  return (
    <Box h='full'>
      <CommonGatedUpgradeModal
        isOpen={isUpgradeModal}
        onClose={onCloseUpgradeModal}
        onClickUpgrade={onClickUpgrade}
        upgradePlan={UserTypes.Business}
        modalImg={BrandKitImage}
        modalType={ModalUpgradeTypeEnum.BrandKit}
      />
      <SingleTabView
        onItemClick={onItemClick}
        disableDragDrop={disableDragDrop}
        handleShuffleColors={handleShuffleColors}
        onOpenUpgradeModal={handleOpenUpgradeModal}
      />
    </Box>
  );
});
BrandKitMenu.displayName = 'BrandTab';

interface BrandKitViewProps {
  onItemClick: (widget: NewWidget | NewWidget[]) => void;
  disableDragDrop?: boolean;
  handleShuffleColors: (colors: string[], isBrandKit?: boolean) => void;
  onOpenUpgradeModal: () => void;
}

const SingleTabView = ({
  onItemClick,
  disableDragDrop,
  handleShuffleColors,
  onOpenUpgradeModal,
}: BrandKitViewProps) => {
  return (
    <Box h='full' overflow='auto'>
      <Box>
        <BrandKitTab
          onLogoClick={onItemClick}
          disableDragDrop={disableDragDrop}
          handleShuffleColors={handleShuffleColors}
          onOpenUpgradeModal={onOpenUpgradeModal}
        />
      </Box>
      <Divider />
      <Box pt={4}>
        <ExploreTab handleShuffleColors={handleShuffleColors} />
      </Box>
      <Box mt={3} mb={7}>
        <ExploreFonts />
      </Box>
    </Box>
  );
};
